// smartRecalculation.js - Intelligent grocery list recalculation system
import { processGroceryList } from './groceryProcessor';
import { processGroceryListAI } from './aiGroceryFallback';

/**
 * Smart grocery list recalculation when recipes are removed
 * Avoids expensive AI calls when only database-matched ingredients are affected
 * 
 * @param {Object} removedRecipe - The recipe being removed
 * @param {Array} remainingRecipes - Recipes remaining in the meal plan
 * @param {Array} currentProductList - Current grocery list products
 * @param {Object} context - Processing context (country, numberOfPeople, budget, etc.)
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} New grocery list
 */
export const smartRecalculateGroceryList = async (
  removedRecipe,
  remainingRecipes,
  currentProductList,
  context,
  onProgress = null
) => {
  try {
    // If no remaining recipes, return empty list
    if (!remainingRecipes || remainingRecipes.length === 0) {
      if (onProgress) {
        onProgress({
          step: 'clearing',
          progress: 100,
          message: 'Clearing grocery list - no recipes remaining'
        });
      }
      return {
        products: [],
        totalCost: '0.00',
        leftovers: [],
        maxStorageTime: ''
      };
    }

    // 1. Extract ingredients from removed recipe
    const removedIngredients = extractIngredientsFromRecipe(removedRecipe);
    
    if (onProgress) {
      onProgress({
        step: 'analyzing',
        progress: 10,
        message: 'Analyzing impact of recipe removal...'
      });
    }

    // 2. Check if any AI-generated products would be affected
    const affectedAIProducts = findAffectedAIProducts(
      removedIngredients, 
      currentProductList
    );

    // 3. Make smart decision
    if (affectedAIProducts.length === 0) {
      console.log('📊 Smart recalculation: Quick path - No AI products affected');
      
      if (onProgress) {
        onProgress({
          step: 'quick_recalculation',
          progress: 50,
          message: 'Quick recalculation using database products...'
        });
      }

      // Quick path: Process only with database products (no AI fallback)
      return await processGroceryListFromDatabase(
        remainingRecipes,
        context,
        onProgress
      );
    } else {
      console.log(`🤖 Smart recalculation: Full path - ${affectedAIProducts.length} AI products affected`);
      console.log('Affected AI products:', affectedAIProducts.map(p => p.name));
      
      if (onProgress) {
        onProgress({
          step: 'full_recalculation',
          progress: 20,
          message: `Full recalculation needed for ${affectedAIProducts.length} AI products...`
        });
      }

      // Full path: Re-run complete AI processing
      return await processGroceryListAI({
        mealPlan: remainingRecipes,
        country: context.country,
        numberOfPeople: context.numberOfPeople,
        budget: context.budget,
        onProgress: (progress) => {
          if (onProgress) {
            onProgress({
              ...progress,
              progress: 20 + (progress.progress * 0.8) // Scale progress from 20-100%
            });
          }
        }
      });
    }

  } catch (error) {
    console.error('❌ Smart recalculation failed:', error);
    
    // Fallback to full recalculation on any error
    console.log('🔄 Fallback: Running full recalculation due to error');
    return await processGroceryListAI({
      mealPlan: remainingRecipes,
      country: context.country,
      numberOfPeople: context.numberOfPeople,
      budget: context.budget,
      onProgress
    });
  }
};

/**
 * Extract ingredient names from a recipe
 * @param {Object} recipe - Recipe object
 * @returns {Array<string>} Array of ingredient names (lowercase)
 */
const extractIngredientsFromRecipe = (recipe) => {
  if (!recipe || !recipe.ingredients) {
    return [];
  }

  return recipe.ingredients.map(ingredient => {
    // Handle different ingredient structures
    const name = ingredient.product || ingredient.name || ingredient;
    return typeof name === 'string' ? name.toLowerCase() : '';
  }).filter(Boolean);
};

/**
 * Find AI-generated products that would be affected by removing specific ingredients
 * @param {Array<string>} removedIngredients - Ingredient names being removed (lowercase)
 * @param {Array<Object>} currentProductList - Current grocery list products
 * @returns {Array<Object>} AI products that would be affected
 */
const findAffectedAIProducts = (removedIngredients, currentProductList) => {
  if (!currentProductList || !Array.isArray(currentProductList)) {
    return [];
  }

  return currentProductList.filter(product => {
    // Only check AI-generated products
    if (product.source !== 'ai') {
      return false;
    }

    // Check if this AI product covers any of the removed ingredients
    const productIngredientNames = product.originalIngredientNames || [product.name];
    
    return removedIngredients.some(removedIngName => 
      productIngredientNames.some(productIngName => {
        const productNameLower = productIngName.toLowerCase();
        return productNameLower.includes(removedIngName) ||
               removedIngName.includes(productNameLower);
      })
    );
  });
};

/**
 * Process grocery list using only database products (no AI fallback)
 * This is the quick path for when no AI products are affected
 * @param {Array} remainingRecipes - Recipes to process
 * @param {Object} context - Processing context
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Grocery list result
 */
const processGroceryListFromDatabase = async (remainingRecipes, context, onProgress) => {
  // Use the existing processGroceryList but skip AI fallback
  // This requires modifying processGroceryList to have a skipAI option
  return await processGroceryList({
    mealPlan: remainingRecipes,
    country: context.country,
    numberOfPeople: context.numberOfPeople,
    budget: context.budget,
    skipAIFallback: true, // New option to skip AI processing
    onProgress
  });
};

/**
 * Smart recalculation for multiple recipe removal
 * @param {Array} removedRecipes - Recipes being removed
 * @param {Array} remainingRecipes - Recipes remaining after removal
 * @param {Array} currentProductList - Current product list
 * @param {Object} context - Processing context
 * @returns {Promise<Object>} Recalculated grocery list or null to fall back to standard processing
 */
export const smartRecalculateBulkRemoval = async (
  removedRecipes,
  remainingRecipes,
  currentProductList,
  context
) => {
  console.log('🔄 Smart bulk recalculation: Analyzing multiple recipe removal', {
    removedCount: removedRecipes.length,
    remainingCount: remainingRecipes.length
  });

  const { onProgress } = context;

  // If removing many recipes, it might be more efficient to use standard processing
  if (removedRecipes.length > 2) {
    console.log('📊 Smart bulk recalculation: Many recipes removed, using standard processing for efficiency');
    return null; // Fall back to standard processing
  }

  // Extract all ingredients from removed recipes
  let allRemovedIngredients = [];
  for (const recipe of removedRecipes) {
    const ingredients = extractIngredientsFromRecipe(recipe);
    allRemovedIngredients = allRemovedIngredients.concat(ingredients);
  }

  if (onProgress) {
    onProgress({
      step: 'analyzing_bulk',
      progress: 10,
      message: `Analyzing impact of removing ${removedRecipes.length} recipes...`
    });
  }

  // Check if any AI-generated products would be affected
  const affectedAIProducts = findAffectedAIProducts(
    allRemovedIngredients,
    currentProductList
  );

  // Make smart decision
  if (affectedAIProducts.length === 0) {
    console.log('📊 Smart bulk recalculation: Quick path - No AI products affected by bulk removal');
    
    if (onProgress) {
      onProgress({
        step: 'bulk_quick_recalculation',
        progress: 50,
        message: 'Quick recalculation for bulk removal using database products...'
      });
    }

    // Quick path: Process only with database products (no AI fallback)
    return await processGroceryListFromDatabase(
      remainingRecipes,
      context,
      onProgress
    );
  } else {
    console.log(`🤖 Smart bulk recalculation: Full path - ${affectedAIProducts.length} AI products affected by bulk removal`);
    console.log('Affected AI products:', affectedAIProducts.map(p => p.name));
    
    // For bulk removal with AI impact, fall back to standard processing
    // as it may be too complex to handle efficiently with smart recalculation
    console.log('📊 Smart bulk recalculation: AI products affected, using standard processing');
    return null; // Fall back to standard processing
  }
};

/**
 * Log smart recalculation decision for debugging and monitoring
 * @param {string} decision - 'quick' or 'full'
 * @param {Array} affectedProducts - Products affected by the change
 * @param {Object} context - Additional context
 */
export const logSmartRecalculationDecision = (decision, affectedProducts = [], context = {}) => {
  const timestamp = new Date().toISOString();
  const logData = {
    timestamp,
    decision,
    affectedProductsCount: affectedProducts.length,
    affectedProductNames: affectedProducts.map(p => p.name),
    recipesRemaining: context.recipesRemaining || 0,
    ...context
  };

  console.log('🔍 Smart Recalculation Decision:', logData);
  
  // Could send to analytics service here
  // analytics.track('smart_recalculation', logData);
};

const smartRecalculationUtils = {
  smartRecalculateGroceryList,
  logSmartRecalculationDecision,
  smartRecalculateBulkRemoval
};

export default smartRecalculationUtils;
